import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  MapPinIcon,
  StarIcon,
  PhoneIcon,
  ClockIcon,
  ShieldCheckIcon,
  HeartHandshakeIcon,
  BedIcon,
  UserCheckIcon,
  ActivityIcon,
  CarIcon,
  WifiIcon,
  CoffeeIcon,
  ParkingMeterIcon,
  NavigationIcon,
  CalendarIcon,
  CheckCircleIcon
} from "lucide-react";
import { clinicService } from "../../services";
import { openWhatsApp } from "../../lib/utils";
import BookAppointmentModal from "../BookAppointmentModal";

const HospitalDetails = () => {
  const { hospitalId } = useParams();
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  useEffect(() => {
    const fetchHospital = async () => {
      try {
        setLoading(true);
        const hospitalData = await clinicService.getClinicById(hospitalId);
        setHospital(hospitalData);
      } catch (err) {
        console.error('Error fetching hospital:', err);
        setError('Failed to load hospital information');
        // Fallback to mock data
        setHospital({
          id: hospitalId,
          name: "TheLivCure Elantis",
          rating: 4.7,
          reviews: 93,
          address: "29, Ring Road, Block L, Lajpat Nagar, Delhi - 110024",
          city: "Delhi",
          phone: "+91-9876543210",
          email: "elantis@pristyncare.com",
          website: "www.pristyncare.com/elantis",
          features: ["NABH Accredited", "24x7 Emergency", "ICU Available", "Pharmacy", "Laboratory"],
          specialties: ["General Surgery", "Orthopedics", "Cardiology", "Neurology", "Gynaecology", "Urology"],
          beds: 150,
          doctors: 25,
          operatingRooms: 8,
          icuBeds: 20,
          established: "2019",
          description: "TheLivCure Elantis is dedicated to revolutionising surgical care. We combine the latest medical technology with highly skilled professionals and a patient-centric approach to transform the healthcare experience. Our state-of-the-art facility offers comprehensive medical services with a focus on minimally invasive procedures.",
          facilities: [
            "Modular Operation Theaters",
            "24/7 Emergency Services", 
            "Intensive Care Unit (ICU)",
            "Digital Radiology & Imaging",
            "In-house Pharmacy",
            "Clinical Laboratory",
            "Blood Bank",
            "Cafeteria & Food Court",
            "Patient Parking",
            "Free WiFi",
            "Patient Lounges",
            "Wheelchair Accessibility"
          ],
          departments: [
            { name: "General Surgery", doctors: 5, beds: 30 },
            { name: "Orthopedics", doctors: 4, beds: 25 },
            { name: "Cardiology", doctors: 3, beds: 20 },
            { name: "Neurology", doctors: 3, beds: 15 },
            { name: "Gynaecology", doctors: 4, beds: 25 },
            { name: "Urology", doctors: 3, beds: 20 },
            { name: "ENT", doctors: 2, beds: 10 },
            { name: "Ophthalmology", doctors: 1, beds: 5 }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHospital();
  }, [hospitalId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hospital information...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !hospital) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="text-red-600 mb-4">
            <MapPinIcon className="w-16 h-16 mx-auto mb-2" />
            <h2 className="text-2xl font-bold">Hospital Not Found</h2>
            <p className="text-gray-600 mt-2">{error || "The requested hospital could not be found."}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Safe derived fields and fallbacks
  const hospitalName = hospital.name || "Hospital";
  const rating = hospital.rating || 0;
  const reviews = hospital.reviews || 0;
  const address = hospital.address || hospital.city || "Address not available";
  const phone = hospital.phone || hospital.emergencyContact || "+919311720045";
  const description = hospital.description || "";
  const features = Array.isArray(hospital.features) ? hospital.features : [];
  // Map specialties to departments if departments don't exist
  const specialties = Array.isArray(hospital.specialties) ? hospital.specialties : [];
  const departments = Array.isArray(hospital.departments) ? hospital.departments : 
    specialties.map(spec => ({ name: spec, doctors: 0, beds: 0 }));
  const facilities = Array.isArray(hospital.facilities) ? hospital.facilities : [];
  const amenities = Array.isArray(hospital.amenities) ? hospital.amenities : [];
  // Combine facilities and amenities for display
  const allFacilities = [...facilities, ...amenities];
  const beds = hospital.beds ?? 0;
  const doctors = hospital.doctors ?? 0;
  const operatingRooms = hospital.operatingRooms ?? 0;
  const icuBeds = hospital.icuBeds ?? 0;
  const established = hospital.established || "N/A";

  const hospitalStats = [
    { icon: BedIcon, number: beds, label: "Total Beds", color: "text-blue-600" },
    { icon: UserCheckIcon, number: doctors, label: "Expert Doctors", color: "text-green-600" },
    { icon: ActivityIcon, number: operatingRooms, label: "Operation Theaters", color: "text-purple-600" },
    { icon: HeartHandshakeIcon, number: icuBeds, label: "ICU Beds", color: "text-red-600" }
  ];

  const patientReviews = [
    {
      name: "Priya Sharma",
      rating: 5,
      date: "January 2025",
      treatment: "Gallbladder Surgery",
      review: "Excellent facility with very professional staff. The surgery went smoothly and the recovery was comfortable. Highly recommend this hospital."
    },
    {
      name: "Rajesh Kumar",
      rating: 5,
      date: "December 2024",
      treatment: "Hernia Repair",
      review: "Outstanding service from admission to discharge. The doctors and nurses were very caring and the facilities are top-notch."
    },
    {
      name: "Sunita Patel",
      rating: 4,
      date: "December 2024",
      treatment: "Cataract Surgery",
      review: "Good experience overall. Clean hospital with modern equipment. The staff was helpful throughout my stay."
    }
  ];

  const getFacilityIcon = (facility) => {
    if (facility.includes("Parking")) return ParkingMeterIcon;
    if (facility.includes("WiFi")) return WifiIcon;
    if (facility.includes("Cafeteria")) return CoffeeIcon;
    if (facility.includes("Emergency")) return HeartHandshakeIcon;
    if (facility.includes("ICU")) return BedIcon;
    return CheckCircleIcon;
  };

  // Handle phone call
  const handleCall = (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    window.location.href = `tel:${cleanPhone}`;
  };

  // Handle get directions
  const handleGetDirections = () => {
    const address = encodeURIComponent(hospital.address || `${hospital.city}, ${hospital.state}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hospital Header */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Hospital Image */}
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
                  {hospital.imageUrl ? (
                    <div className="text-center w-full">
                      <img
                        src={hospital.imageUrl}
                        alt={hospital.name || 'Hospital'}
                        className="w-full max-w-xs h-64 object-cover rounded-2xl shadow-lg mb-4 mx-auto"
                      />
                      <Badge className="bg-blue-600 text-white">{hospital.type || 'Super Specialty Hospital'}</Badge>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                        <HeartHandshakeIcon className="w-16 h-16 text-white" />
                      </div>
                      <Badge className="bg-blue-600 text-white">{hospital.type || 'Super Specialty Hospital'}</Badge>
                    </div>
                  )}
                </div>

                {/* Hospital Info */}
                <div className="lg:col-span-2 p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                    <div>
                      <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{hospital.name}</h1>
                      <div className="flex items-center space-x-1 mb-3">
                        <StarIcon className="w-5 h-5 fill-green-500 text-green-500" />
                        <span className="font-semibold text-green-600">{hospital.rating}/5</span>
                        <span className="text-gray-500">({hospital.reviews} reviews)</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {hospital.type && (
                        <Badge variant="secondary" className="text-xs">
                          {hospital.type}
                        </Badge>
                      )}
                      {hospital.is24Hours && (
                        <Badge variant="secondary" className="text-xs">
                          24/7 Available
                        </Badge>
                      )}
                      {hospital.isEmergencyCenter && (
                        <Badge variant="secondary" className="text-xs">
                          Emergency Center
                        </Badge>
                      )}
                      {hospital.hasParking && (
                        <Badge variant="secondary" className="text-xs">
                          Parking Available
                        </Badge>
                      )}
                      {hospital.hasWheelchairAccess && (
                        <Badge variant="secondary" className="text-xs">
                          Wheelchair Accessible
                        </Badge>
                      )}
                      {features.slice(0, 3).map((feature) => (
                        <Badge key={feature} variant="secondary" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                      <p className="text-gray-600">{hospital.address}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="w-5 h-5 text-gray-400" />
                      <button
                        type="button"
                        onClick={() => openWhatsApp(phone, "Hi, I am contacting you for treatment from TheLivCure site.")}
                        className="text-gray-700 underline underline-offset-4 hover:opacity-80"
                      >
                        {phone}
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <p className="text-gray-600">24/7 Emergency Services Available</p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">{hospital.description}</p>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button 
                      size="lg" 
                      className="bg-gray-900 hover:bg-gray-600 text-sm sm:text-base" 
                      onClick={() => handleCall(phone)}
                    >
                      <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Call Hospital
                    </Button>
                    <Button 
                      size="lg" 
                      className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base"
                      onClick={() => setIsBookingOpen(true)}
                    >
                      <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Book Appointment
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="text-sm sm:text-base"
                      onClick={handleGetDirections}
                    >
                      <NavigationIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Get Directions
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Hospital Statistics */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {hospitalStats.map((stat, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <div className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Departments / Specialties */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {departments.length > 0 ? "Medical Departments" : "Medical Specialties"}
                  </h2>
                  {departments.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {departments.map((dept, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                          <h3 className="font-semibold text-gray-900 mb-2">
                            {typeof dept === 'string' ? dept : dept.name}
                          </h3>
                          {typeof dept === 'object' && (
                            <div className="flex justify-between text-sm text-gray-600">
                              <span>{dept.doctors || 0} Doctors</span>
                              <span>{dept.beds || 0} Beds</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : specialties.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {specialties.map((specialty, index) => (
                        <Badge key={index} variant="secondary" className="text-sm">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No specialties or departments listed.</p>
                  )}
                </CardContent>
              </Card>

              {/* Facilities */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Hospital Facilities</h2>
                  {allFacilities.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {allFacilities.map((facility, index) => {
                        const IconComponent = getFacilityIcon(facility);
                        return (
                          <div key={index} className="flex items-center space-x-2 sm:space-x-3">
                            <IconComponent className="w-5 h-5 text-blue-600" />
                            <span className="text-gray-700">{facility}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {hospital.hasParking && (
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <ParkingMeterIcon className="w-5 h-5 text-blue-600" />
                          <span className="text-gray-700">Parking Available</span>
                        </div>
                      )}
                      {hospital.hasWheelchairAccess && (
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                          <span className="text-gray-700">Wheelchair Accessible</span>
                        </div>
                      )}
                      {hospital.is24Hours && (
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <ClockIcon className="w-5 h-5 text-blue-600" />
                          <span className="text-gray-700">24/7 Services</span>
                        </div>
                      )}
                      {hospital.isEmergencyCenter && (
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <HeartHandshakeIcon className="w-5 h-5 text-blue-600" />
                          <span className="text-gray-700">Emergency Center</span>
                        </div>
                      )}
                      {allFacilities.length === 0 && !hospital.hasParking && !hospital.hasWheelchairAccess && !hospital.is24Hours && !hospital.isEmergencyCenter && (
                        <p className="text-gray-500">No facilities listed.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Patient Reviews */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Patient Reviews</h2>
                  <div className="space-y-6">
                    {patientReviews.map((review, index) => (
                      <div key={index} className="border-b border-gray-100 pb-6 last:border-b-0">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="font-semibold text-gray-900">{review.name}</div>
                            <div className="text-sm text-gray-500">
                              {review.treatment} • {review.date}
                            </div>
                          </div>
                          <div className="flex">
                            {[...Array(review.rating)].map((_, i) => (
                              <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600">{review.review}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => setIsBookingOpen(true)}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Book Appointment
                    </Button>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700" 
                      onClick={() => handleCall(phone)}
                    >
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      Call: {phone}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleGetDirections}
                    >
                      <NavigationIcon className="w-4 h-4 mr-2" />
                      Get Directions
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleCall("102")}
                    >
                      Emergency: 102
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Hospital Info */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Hospital Information</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Established:</span>
                      <span className="font-medium">{hospital.established}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Beds:</span>
                      <span className="font-medium">{hospital.beds}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Doctors:</span>
                      <span className="font-medium">{hospital.doctors}+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Operation Theaters:</span>
                      <span className="font-medium">{hospital.operatingRooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ICU Beds:</span>
                      <span className="font-medium">{hospital.icuBeds}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Accreditations */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Accreditations</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                      <span className="text-sm">NABH Accredited</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
                      <span className="text-sm">ISO 9001:2015 Certified</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
                      <span className="text-sm">Green Hospital Certified</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-red-800 mb-4">Emergency Contact</h3>
                  <div className="space-y-2 text-sm">
                    <div className="text-red-700">
                      For medical emergencies, call immediately:
                    </div>
                    <div className="text-xl font-bold text-red-800">
                      {phone}
                    </div>
                    <div className="text-red-600">
                      Available 24/7
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Location Map Placeholder */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Location</h2>
              <div className="h-64 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <MapPinIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Interactive Map View</p>
                  <p className="text-sm text-gray-500">{hospital.address}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />

      {/* Book Appointment Modal */}
      <BookAppointmentModal
        isOpen={isBookingOpen}
        onOpenChange={setIsBookingOpen}
        treatmentType="General Consultation"
      />
    </div>
  );
};

export default HospitalDetails;